import { Checkbox, Popover } from '@mui/material';
import { forwardRef, Fragment, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DifferencesPopover from './DifferencesPopover';
import EastIcon from '@mui/icons-material/East';
import IssuesPopover from './IssuesPopover';
import WarningsPopover from './WarningsPopover';
import SimilarArticlesCell from './SimilarArticlesCell';
import { Tooltip } from 'react-tooltip';
import { SHEET_ARTICULOS } from '../constantsArticleExcel';
import { formatEuro } from '../../../../utils/format';

const getField = (obj = {}, field) => {
  let value = obj[field];
  if (value === null || value === undefined) value = obj[`${field}Og`];
  if (value === null || value === undefined) value = '';
  return value;
};

const getCellClass = (item, field) => {
  if (item?.type === 'edited' && item?.changedKeys?.includes(field)) return 'edited-cell';
  if (item?.type === 'with-issues' && item?.issues?.some((issue) => issue?.field === field)) return 'with-issues-cell';
  return '';
};

const ArticlesT2Row = forwardRef(
  (
    {
      article,
      formats,
      isSelected,
      onToggle,
      isDisabled,
      selectedFormatKeys,
      onToggleFormat,
      onToggleAllFormats,
      selectedProviderKeys,
      onToggleProvider,
      onToggleAllProviders,
      selectedDeletedPFIds,
      onToggleDeletedPF,
      markedDuplicateCode,
      onMarkDuplicate,
      'data-index': dataIndex,
    },
    ref,
  ) => {
    const [expanded, setExpanded] = useState(false);
    const [expandedFormats, setExpandedFormats] = useState({});
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const toggleFormatExpanded = (key) => setExpandedFormats((prev) => ({ ...prev, [key]: !prev[key] }));

    const rowClass =
      article?.type === 'edited'
        ? 'edited'
        : article?.type === 'created'
          ? 'created'
          : article?.type === 'with-issues'
            ? 'with-issues'
            : '';

    return (
      <tbody ref={ref} data-index={dataIndex}>
        <tr className={`${rowClass} ${markedDuplicateCode ? 'marked-duplicated' : ''}`}>
          <td>
            <div>
              <Checkbox
                size="small"
                checked={isSelected ?? false}
                disabled={isDisabled || !!markedDuplicateCode}
                onChange={() => {
                  onToggle?.();
                  const selectableFormatKeys = formats
                    .filter((f) => f.type !== 'with-out-changes' && f.type !== 'with-issues')
                    .map((f) => f._excelRow?.toString())
                    .filter(Boolean);
                  const allFormatKeys = formats.map((f) => f._excelRow?.toString()).filter(Boolean);
                  onToggleAllFormats?.(isSelected ? allFormatKeys : selectableFormatKeys, !isSelected);

                  const selectableProviderRows = formats
                    .flatMap((f) => f.providers ?? [])
                    .filter((p) => p.type !== 'with-out-changes' && p.type !== 'with-issues')
                    .map((p) => p._excelRow?.toString())
                    .filter(Boolean);
                  const allProviderRows = formats
                    .flatMap((f) => f.providers ?? [])
                    .map((p) => p._excelRow?.toString())
                    .filter(Boolean);
                  onToggleAllProviders?.(isSelected ? allProviderRows : selectableProviderRows, !isSelected);
                }}
              />
            </div>
          </td>
          <td className={getCellClass(article, 'code')}>
            <div>
              {article?.type === 'with-issues' && (
                <IssuesPopover issues={article?.issues} excelRow={article?.excelRow} excelSheet={article?.excelSheet} />
              )}
              <WarningsPopover
                warnings={[
                  ...(article?.warnings ?? []),
                  ...formats.flatMap((f) =>
                    (f.warnings ?? []).map((w) => ({ ...w, message: `[Fila ${f._excelRow}] ${w.message}` })),
                  ),
                ]}
                excelRow={article?.excelRow}
                excelSheet={SHEET_ARTICULOS}
              />
              {getField(article, 'code')}
            </div>
          </td>

          <td className={getCellClass(article, 'imglink')}>
            <div>
              {article.imgFile || getField(article, 'imglink') || article.imgBase64 ? (
                <img
                  style={{ height: '50px', objectFit: 'contain', border: '1px solid #3c4145' }}
                  src={
                    article.imgFile
                      ? URL.createObjectURL(article.imgFile)
                      : article.imgBase64 || getField(article, 'imglink')
                  }
                  alt="img"
                />
              ) : (
                <>
                  <span
                    aria-owns={open ? 'mouse-over-t2-img' : undefined}
                    aria-haspopup="true"
                    onMouseEnter={(e) => setAnchorEl(e.currentTarget)}
                    onMouseLeave={() => setAnchorEl(null)}
                  >
                    {getField(article, 'imglink') || <i>vacío</i>}
                  </span>
                  {getField(article, 'imglink') && (
                    <Popover
                      id="mouse-over-t2-img"
                      sx={{ pointerEvents: 'none' }}
                      open={open}
                      anchorEl={anchorEl}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                      onClose={() => setAnchorEl(null)}
                      disableRestoreFocus
                    >
                      <img src={getField(article, 'imglink')} alt="img" width="130rem" />
                    </Popover>
                  )}
                </>
              )}
            </div>
          </td>

          <td className={getCellClass(article, 'linkEditable')}>
            <div>
              {getField(article, 'linkEditable') ? (
                <a
                  href={getField(article, 'linkEditable')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="excel-link"
                >
                  Ver arte final
                </a>
              ) : (
                <i>vacío</i>
              )}
              <DifferencesPopover show={article?.type === 'edited' && article?.changedKeys?.includes('linkEditable')}>
                <div>
                  <span>
                    {article?.linkEditableOg ? (
                      <a href={article.linkEditableOg} target="_blank" rel="noopener noreferrer" className="excel-link">
                        Ver arte final
                      </a>
                    ) : (
                      <i>vacío</i>
                    )}
                  </span>{' '}
                  <EastIcon />{' '}
                  <span>
                    {article?.linkEditable ? (
                      <a href={article.linkEditable} target="_blank" rel="noopener noreferrer" className="excel-link">
                        Ver arte final
                      </a>
                    ) : (
                      <i>vacío</i>
                    )}
                  </span>
                </div>
              </DifferencesPopover>
            </div>
          </td>

          <td className={getCellClass(article, 'name')}>
            <div>
              <span
                className={`truncate-name tooltip-artefinal-name-${article.code}`}
                data-tooltip-id={`tooltip-artefinal-name-${article.code}`}
              >
                {getField(article, 'name') || <i>vacío</i>}
              </span>

              <Tooltip id={`tooltip-artefinal-name-${article.code}`} style={{ zIndex: 1000 }}>
                {getField(article, 'name')}
              </Tooltip>
              <DifferencesPopover show={article?.type === 'edited' && article?.changedKeys?.includes('name')}>
                <div>
                  <span>{article?.nameOg}</span> <EastIcon /> <span>{article?.name}</span>
                </div>
              </DifferencesPopover>
            </div>
          </td>

          <td className={getCellClass(article, 'description')}>
            <div>
              <span
                className="truncate-name"
                data-tooltip-id={`tooltip-description-t2-${article?.code ?? article?.excelRow}`}
              >
                {getField(article, 'description') || <i>vacío</i>}
              </span>
              {getField(article, 'description') && (
                <Tooltip
                  id={`tooltip-description-t2-${article?.code ?? article?.excelRow}`}
                  style={{
                    maxWidth: '360px',
                    width: 'auto',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    zIndex: 1100,
                  }}
                >
                  {getField(article, 'description')}
                </Tooltip>
              )}
              <DifferencesPopover show={article?.type === 'edited' && article?.changedKeys?.includes('description')}>
                <div>
                  <span>{article?.descriptionOg || <i>vacío</i>}</span> <EastIcon />{' '}
                  <span>{article?.description || <i>vacío</i>}</span>
                </div>
              </DifferencesPopover>
            </div>
          </td>

          <SimilarArticlesCell
            similarArticles={article?.similarArticles}
            markedDuplicateCode={markedDuplicateCode}
            onMarkDuplicate={onMarkDuplicate}
            name={getField(article, 'name')}
            exactMatchCode={article?.type === 'edited' ? article?.code?.toString() : undefined}
          />

          <td className={getCellClass(article, 'categories')}>
            <div>
              <span
                className="truncate-name"
                data-tooltip-id={`tooltip-categories-t2-${article?.code ?? article?.excelRow}`}
              >
                {article?.categoriesNames?.join(', ') || <i>vacío</i>}
              </span>
              {article?.categoriesNames?.length > 0 && (
                <Tooltip
                  id={`tooltip-categories-t2-${article?.code ?? article?.excelRow}`}
                  style={{
                    maxWidth: '360px',
                    width: 'auto',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    zIndex: 1100,
                  }}
                >
                  {article?.categoriesNames?.join(', ')}
                </Tooltip>
              )}
              <DifferencesPopover show={article?.type === 'edited' && article?.changedKeys?.includes('categories')}>
                <div>
                  <span>{(article?.categoriesOg || []).map((c) => c?.name || c).join(', ') || <i>vacío</i>}</span>{' '}
                  <EastIcon /> <span>{article?.categoriesNames?.join(', ') || <i>vacío</i>}</span>
                </div>
              </DifferencesPopover>
            </div>
          </td>

          <td
            className={getCellClass(article, 'centerType')}
            style={
              getField(article, 'hipermercado')
                ? { background: '#c6efce', color: '#2c612e' }
                : { background: '#f4e0e0', color: '#c80053' }
            }
          >
            <div>
              {getField(article, 'hipermercado') ? 'SI' : 'NO'}
              <DifferencesPopover show={article?.type === 'edited' && article?.changedKeys?.includes('centerType')}>
                <div>
                  <span>{article?.hipermercadoOg ? 'SI' : 'NO'}</span> <EastIcon />{' '}
                  <span>{article?.hipermercado ? 'SI' : 'NO'}</span>
                </div>
              </DifferencesPopover>
            </div>
          </td>

          <td
            className={getCellClass(article, 'centerType')}
            style={
              getField(article, 'proximidad')
                ? { background: '#c6efce', color: '#2c612e' }
                : { background: '#f4e0e0', color: '#c80053' }
            }
          >
            <div>
              {getField(article, 'proximidad') ? 'SI' : 'NO'}
              <DifferencesPopover show={article?.type === 'edited' && article?.changedKeys?.includes('centerType')}>
                <div>
                  <span>{article?.proximidadOg ? 'SI' : 'NO'}</span> <EastIcon />{' '}
                  <span>{article?.proximidad ? 'SI' : 'NO'}</span>
                </div>
              </DifferencesPopover>
            </div>
          </td>

          <td
            className={getCellClass(article, 'centerType')}
            style={
              getField(article, 'ultraproximidad')
                ? { background: '#c6efce', color: '#2c612e' }
                : { background: '#f4e0e0', color: '#c80053' }
            }
          >
            <div>
              {getField(article, 'ultraproximidad') ? 'SI' : 'NO'}
              <DifferencesPopover show={article?.type === 'edited' && article?.changedKeys?.includes('centerType')}>
                <div>
                  <span>{article?.ultraproximidadOg ? 'SI' : 'NO'}</span> <EastIcon />{' '}
                  <span>{article?.ultraproximidad ? 'SI' : 'NO'}</span>
                </div>
              </DifferencesPopover>
            </div>
          </td>

          <td
            className={getCellClass(article, 'isMontaje')}
            style={
              getField(article, 'isMontaje')
                ? { background: '#c6efce', color: '#2c612e' }
                : { background: '#f4e0e0', color: '#c80053' }
            }
          >
            <div>{getField(article, 'isMontaje') ? 'SI' : 'NO'}</div>
          </td>

          <td
            className={getCellClass(article, 'active')}
            style={
              getField(article, 'active')
                ? { background: '#c6efce', color: '#2c612e' }
                : { background: '#f4e0e0', color: '#c80053' }
            }
          >
            <div>{getField(article, 'active') ? 'SI' : 'NO'}</div>
          </td>

          <td className={getCellClass(article, 'minQuantity')}>
            <div>{getField(article, 'minQuantity') || 1}</div>
          </td>

          <td className={getCellClass(article, 'tags')}>
            <div>
              {article?.tags?.join(', ')}
              <DifferencesPopover show={article?.type === 'edited' && article?.changedKeys?.includes('tags')}>
                <div>
                  <span>{article?.tagsOg?.join(', ')}</span> <EastIcon /> <span>{article?.tags?.join(', ')}</span>
                </div>
              </DifferencesPopover>
            </div>
          </td>

          <td className={getCellClass(article, 'typeCommunication')}>
            <div>
              {getField(article, 'typeCommunication') || <i>vacío</i>}
              <DifferencesPopover
                show={article?.type === 'edited' && article?.changedKeys?.includes('typeCommunication')}
              >
                <div>
                  <span>{article?.typeCommunicationOg || <i>vacío</i>}</span> <EastIcon />{' '}
                  <span>{article?.typeCommunication || <i>vacío</i>}</span>
                </div>
              </DifferencesPopover>
            </div>
          </td>

          <td>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.3rem',
                cursor: formats.length > 0 ? 'pointer' : 'default',
              }}
              onClick={() => formats.length > 0 && setExpanded((e) => !e)}
            >
              <strong>
                {formats.length} formato{formats.length !== 1 ? 's' : ''}
              </strong>
              {formats.length > 0 &&
                (expanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />)}
            </div>
          </td>
          <td />
          <td />
          <td />
          <td />
          <td />
        </tr>

        {expanded &&
          formats.map((fmt, i) => {
            const fmtKey = fmt._excelRow?.toString();
            const providers = fmt.providers ?? [];
            const deletedProviders = fmt.deletedProviders ?? [];
            const fmtExpanded = expandedFormats[fmtKey] ?? true;
            const providerCount = providers.length + deletedProviders.length;

            const selectableProviderKeys = providers
              .filter((p) => p.type !== 'with-out-changes' && p.type !== 'with-issues')
              .map((p) => p._excelRow?.toString())
              .filter(Boolean);
            const allProviderKeys = providers.map((p) => p._excelRow?.toString()).filter(Boolean);
            const fmtAllProvidersSelected =
              selectableProviderKeys.length > 0 && selectableProviderKeys.every((k) => selectedProviderKeys?.has(k));
            const fmtSomeProvidersSelected = selectableProviderKeys.some((k) => selectedProviderKeys?.has(k));

            return (
              <Fragment key={`${fmt.codeFormat ?? fmtKey ?? i}-${i}`}>
                <tr
                  className={`t2-format-subrow ${
                    fmt.type === 'created' ? 'created' : fmt.type === 'edited' ? 'edited' : ''
                  }`}
                >
                  <td style={{ borderRight: 'none', background: 'transparent' }}>
                    <Checkbox
                      size="small"
                      checked={selectedFormatKeys?.has(fmtKey) ?? false}
                      indeterminate={
                        !selectedFormatKeys?.has(fmtKey) && fmtSomeProvidersSelected && !fmtAllProvidersSelected
                      }
                      onChange={() => {
                        const willSelect = !selectedFormatKeys?.has(fmtKey);
                        onToggleFormat?.(fmtKey);
                        onToggleAllProviders?.(willSelect ? selectableProviderKeys : allProviderKeys, willSelect);
                      }}
                      disabled={fmt.type === 'with-out-changes' || fmt.type === 'with-issues'}
                    />
                  </td>
                  {Array.from({ length: 15 }).map((_, j) => (
                    <td
                      key={`${fmt.codeFormat ?? fmtKey ?? i}-empty-${j}`}
                      style={{ borderRight: 'none', background: 'transparent' }}
                    />
                  ))}
                  <td className={fmt.type === 'edited' && fmt.changedKeys?.includes('codeFormat') ? 'edited-cell' : ''}>
                    <div>
                      <WarningsPopover
                        warnings={fmt?.warnings}
                        excelRow={fmt?._excelRow}
                        excelSheet={SHEET_ARTICULOS}
                      />
                      {fmt.codeFormat || <i>vacío</i>}
                    </div>
                  </td>
                  <td className={fmt.type === 'edited' && fmt.changedKeys?.includes('format') ? 'edited-cell' : ''}>
                    <div>{fmt.format || <i>vacío</i>}</div>
                  </td>
                  <td
                    className={fmt.type === 'edited' && fmt.changedKeys?.includes('materialName') ? 'edited-cell' : ''}
                  >
                    <div>{fmt.materialName || <i>vacío</i>}</div>
                  </td>
                  <td colSpan={3}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.3rem',
                        cursor: providerCount > 0 ? 'pointer' : 'default',
                      }}
                      onClick={() => providerCount > 0 && toggleFormatExpanded(fmtKey)}
                    >
                      <strong>
                        {providerCount} proveedor{providerCount !== 1 ? 'es' : ''}
                      </strong>
                      {providerCount > 0 &&
                        (fmtExpanded ? (
                          <KeyboardArrowUpIcon fontSize="small" />
                        ) : (
                          <KeyboardArrowDownIcon fontSize="small" />
                        ))}
                    </div>
                  </td>
                </tr>

                {fmtExpanded &&
                  providers.map((p, k) => {
                    const provKey = p._excelRow?.toString();
                    return (
                      <tr
                        key={`pf-${fmtKey}-${provKey}-${k}`}
                        className={`t2-provider-subrow ${
                          p.type === 'created'
                            ? 'created'
                            : p.type === 'edited'
                              ? 'edited'
                              : p.type === 'with-issues'
                                ? 'with-issues'
                                : ''
                        }`}
                      >
                        <td style={{ borderRight: 'none', background: 'transparent' }}>
                          <Checkbox
                            size="small"
                            checked={selectedProviderKeys?.has(provKey) ?? false}
                            onChange={() => onToggleProvider?.(provKey)}
                            disabled={p.type === 'with-out-changes' || p.type === 'with-issues'}
                          />
                        </td>
                        {Array.from({ length: 18 }).map((_, j) => (
                          <td
                            key={`pf-${fmtKey}-${provKey}-empty-${j}`}
                            style={{ borderRight: 'none', background: 'transparent' }}
                          />
                        ))}
                        <td
                          className={
                            p.type === 'edited' && p.changedKeys?.includes('providerName') ? 'edited-cell' : ''
                          }
                        >
                          <div>
                            {p.type === 'with-issues' && (
                              <IssuesPopover issues={p?.issues} excelRow={p?.excelRow} excelSheet={p?.excelSheet} />
                            )}
                            <WarningsPopover
                              warnings={p?.warnings}
                              excelRow={p?._excelRow}
                              excelSheet={SHEET_ARTICULOS}
                            />
                            {p.providerName || <i>vacío</i>}
                          </div>
                        </td>
                        <td className={p.type === 'edited' && p.changedKeys?.includes('code') ? 'edited-cell' : ''}>
                          <div>{p.code || <i>vacío</i>}</div>
                        </td>
                        <td className={p.type === 'edited' && p.changedKeys?.includes('price') ? 'edited-cell' : ''}>
                          <div>{formatEuro(p.price) ?? <i>vacío</i>}</div>
                        </td>
                      </tr>
                    );
                  })}

                {fmtExpanded &&
                  deletedProviders.map((d, k) => {
                    const delId = d._id?.toString();
                    return (
                      <tr
                        key={`pf-del-${fmtKey}-${delId}-${k}`}
                        className="t2-provider-subrow"
                        style={{ background: '#fff3f3' }}
                      >
                        <td style={{ borderRight: 'none', background: 'transparent' }}>
                          <Checkbox
                            size="small"
                            checked={selectedDeletedPFIds?.has(delId) ?? false}
                            onChange={() => onToggleDeletedPF?.(delId)}
                          />
                        </td>
                        {Array.from({ length: 18 }).map((_, j) => (
                          <td
                            key={`pf-del-${fmtKey}-${delId}-empty-${j}`}
                            style={{ borderRight: 'none', background: 'transparent' }}
                          />
                        ))}
                        <td>
                          <div style={{ color: '#c50202', textDecoration: 'line-through' }}>
                            {d.providerName} <small>(eliminar)</small>
                          </div>
                        </td>
                        <td style={{ color: '#c50202', textDecoration: 'line-through' }}>{d.code}</td>
                        <td style={{ color: '#c50202', textDecoration: 'line-through' }}>
                          {formatEuro(d.price) ?? d.price}
                        </td>
                      </tr>
                    );
                  })}
              </Fragment>
            );
          })}
      </tbody>
    );
  },
);

export default ArticlesT2Row;
